<script setup lang="ts">
    import { useRoute } from 'vue-router';
    import type { TopSubscriber } from '../../interfaces';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import List from '@shared/components/List/List.vue';
    import { useGravatar } from '@shared/utils/use-gravatar';

    const get_gravatar = useGravatar({ withPlaceholder: true });

    const props = defineProps<{
        topSubscribers: TopSubscriber[];
    }>();

    const route = useRoute();

    const guidEmpty = '00000000-0000-0000-0000-000000000000';

    function displayName(sub: TopSubscriber) {
        return [sub.FirstName, sub.LastName].filter(Boolean).join(' ').trim() || $Resources.NoContactNamePlaceholder;
    }

    function getPhotoUrl(sub: TopSubscriber) {
        return get_gravatar(sub.Email, 40);
    }

    const orderOptions = () => {
        return [
            { display: $Resources.OpenedLabel, key: 'OpenCount' },
            { display: $Resources.IssueStatisticsUniqueClicksHeading, key: 'ClickCount' },
            { display: $Resources.IssueStatisticsTotalClicksHeading, key: 'TotalClickCount' },
        ];
    };
</script>

<template>
    <div class="print-block">
        <h2 class="h3 m-0-b">{{ $Resources.MostEngaged }}</h2>

        <BsAlert
            class="m-sm-t"
            :show="topSubscribers.length === 0"
            :type="AlertTypes.Info"
            :dismissible="false"
            :content="$Resources.IssueStatisticsNoEngagementMessage" />

        <List
            v-if="topSubscribers.length > 0"
            class="th3-text-right th4-text-right"
            display-as="table"
            itemId="ContactId"
            :items="topSubscribers"
            :table-headers="[
                '',
                $Resources.RecipientsLabel,
                $Resources.OpenedLabel,
                $Resources.IssueStatisticsUniqueClicksHeading,
                $Resources.IssueStatisticsTotalClicksHeading,
            ]"
            :per-page="10"
            :filter-keys="['FirstName', 'LastName', 'Email']"
            :orderOptions="orderOptions()">
            <template #item="{ item }">
                <td class="autofit">
                    <img :src="getPhotoUrl(item)" alt="avatar" class="img-circle gravatar-fallback subscriber-photo" />
                </td>
                <td>
                    <RouterLink v-if="item.ContactId !== guidEmpty" :to="{ name: 'Contact', params: { contactId: item.ContactId } }">
                        {{ displayName(item) }}
                    </RouterLink>
                    <span v-else>{{ displayName(item) }}</span>
                    <template v-if="item.Email">
                        <br />
                        {{ item.Email }}
                    </template>
                </td>
                <td class="text-right strong">{{ item.OpenCount }}</td>
                <td class="text-right strong">{{ item.ClickCount }}</td>
                <td class="text-right strong">{{ item.TotalClickCount }}</td>
            </template>
        </List>
    </div>
</template>

<style>
    .subscriber-photo {
        width: 40px;
        height: 40px;
        object-fit: cover;
    }
</style>
